// Serve API calls from the host the page itself was loaded from. The session
// cookie is host-scoped, so a page on realpropertydata.ca calling
// www.realpropertydata.ca goes out cross-origin without it and Drupal answers as
// anonymous (current_user -> {status: 0}). Local dev (localhost) and any other
// host fall back to the canonical www host.
const PROD_HOST = "www.realpropertydata.ca";
const isRpdHost =
  typeof window !== "undefined" &&
  /^(www\.)?realpropertydata\.ca$/.test(window.location.hostname);
const SITE_ORIGIN = isRpdHost ? window.location.origin : "https://" + PROD_HOST;

export const BACKEND_URL = SITE_ORIGIN + "/backend/";
export const ROOT_URL = SITE_ORIGIN + "/";

// LinkedIn sign-in. `returnTo` (a path without the leading slash) is where the
// backend lands the reader afterwards; it only honours paths whitelisted in
// linkedin_rpd_return_paths() (contrib/linkedin/linkedin.inc).
export const linkedInLoginUrl = (returnTo) =>
  BACKEND_URL + "linkedin/login/0" + (returnTo ? "?rpd_return=" + encodeURIComponent(returnTo) : "");
export const NO_SLASH_TRAIL_ROOT_URL = SITE_ORIGIN;
export const FILES = BACKEND_URL + "sites/default/files/";

// ── Market Overview server-side paging ──────────────────────
// Paginated replacement for the two unpaginated api/get_projects_utc_new calls.
// Payload shape lives in src/composables/marketOverviewQuery.js; the request is
// issued from src/store/MarketOverviewStore.js.
export const MARKET_OVERVIEW_PAGED_ENDPOINT = "api/get_projects_utc_new_paged";

// Master switch for Market Overview paging. While false the page keeps its
// local pagination and the existing unpaginated fetch, so nothing changes until
// the endpoint above is live. Flip to true to move paging, sorting and every
// filter to the server.
export const MARKET_OVERVIEW_REMOTE_PAGINATION = true;

// Upper bound for the "every matching row" request behind the map, City Preview
// and CSV/PDF export. High enough to cover a realistic full-region result set,
// low enough that a cleared filter set can't ask for the entire table.
export const MARKET_OVERVIEW_EXPORT_LIMIT = 5000;

// ── Chat Bot ────────────────────────────────────────────────
// The floating bot asks one question per Market Overview filter and turns the
// answers into SQL server-side. `filters` serves the question script and the
// options each question accepts; `query` takes the answers and returns the
// generated statement alongside the rows it selected. Both live in the backend
// module's chatbot_api.inc.
export const CHATBOT_FILTERS_ENDPOINT = "api/chatbot/filters";
export const CHATBOT_QUERY_ENDPOINT = "api/chatbot/query";

// Page size for the bot's own preview list, and the ceiling for the "every
// matching row" request the exports and the results page issue. The ceiling
// mirrors PRESALEDB_CHATBOT_MAX_LIMIT in chatbot_api.inc — asking for more is
// clamped server-side, so keeping them equal avoids a silently short export.
export const CHATBOT_PREVIEW_LIMIT = 25;
export const CHATBOT_EXPORT_LIMIT = 5000;

// How many quarters a trend covers by default. The window ends at the latest
// CLOSED quarter — the backend anchors it there rather than at today's, because
// a running quarter would put a partial figure beside full ones.
export const TRENDING_QUARTER_COUNT = 10;

// ---------------------------------------------------------------------------
// Quarterly Trending Analysis (/quarterly-trending-analysis).
//
// Backed by quarterly_trending_api.inc — its own absorption and sale-record
// implementations, its own cache.
//
// Every endpoint takes: end=YYYYQn, quarters=N, tenure=Strata|Rental|Both,
// scope=metro|all|region:<name>|city:<name>, plus the Market Overview scope
// query (city[]/type[]/unittype[]/status[]) that decides which projects exist.
// ---------------------------------------------------------------------------

// Chart 1. Three equal-length year-over-year windows against one dimension,
// reported as units or as an absorption RATE. The rate is the finding: townhomes
// have run volume +52% and rate -58% in the same window, because the tracked
// stock grew faster than the sales did.
export const MARKET_CHARTS_PERIOD_COMPARISON_ENDPOINT = "api/market-charts/period-comparison";

// How many year-over-year windows that chart draws by default, and the fewest
// it ever draws. THIS CARD IS SIZED IN YEARS, not in quarters: the endpoint
// takes `periods` and builds N calendar years back from the anchor's year, so
// TRENDING_QUARTER_COUNT above says nothing about how far back this axis
// reaches. A window that must open on a given quarter is converted to a year
// count against the anchor — see comparisonPeriodCount.
//
// The endpoint clamps `periods` as well. Its ceiling has to stay above the
// longest window asked for here (rental from 2020 is 7 on a 2026 anchor and
// grows by one a year), or the axis stops short of the start the coverage note
// names, with nothing on screen saying it was refused.
export const MARKET_CHARTS_PERIOD_COUNT = 3;

// Chart 2. Ten quarters by dimension. Municipality comes back with
// data.render === "table" — eighteen series with a ~134x spread has no shared
// scale that separates them, so the page draws a sorted table and says why.
export const MARKET_CHARTS_ABSORPTION_TREND_ENDPOINT = "api/market-charts/absorption-trend";

// Chart 3. Strata and rental stacked per quarter, with projects_reporting per
// quarter per tenure — the rental share rises mostly because rental COVERAGE
// rises, and the chart is dishonest without that count beside it.
export const MARKET_CHARTS_TENURE_ENDPOINT = "api/market-charts/absorbed-by-tenure";

// Chart 4. Net PSF over ten quarters by unit type. THE ONLY CHART ON THE
// SALE-RECORD BASIS — it will not tie to the absorption charts and is not meant
// to. Ships both an absolute and an indexed scale; the indexed one is the
// default, because three unit types hundreds of dollars apart cannot be read on
// one axis.
export const MARKET_CHARTS_UNIT_PRICING_ENDPOINT = "api/market-charts/unit-pricing-trend";

// Chart 5. Completions by quarter, recorded through the anchor and scheduled
// after it. Needs pstatus[] — the pipeline counts a WIDER project scope than
// sales, because a project is permitted before it opens for sale. Without it the
// endpoint returns a scope_warning and the page prints it.
export const MARKET_CHARTS_COMPLETION_OUTLOOK_ENDPOINT = "api/market-charts/completion-outlook";

// Chart 6. Standing Inventory Tracker: built, unsold developer units in today's
// quarter (the Unit Mix tab's Standing Inventory) and a projection of what joins
// them over the next eight quarters. Takes the same wider pstatus[] scope as the
// completion outlook, because Selling Soon and Leasing Soon sit outside the sales
// status set.
export const MARKET_CHARTS_STANDING_INVENTORY_ENDPOINT = "api/market-charts/standing-inventory";

// The drill behind every mark. Filters the same records the aggregation
// consumed, on the same keys — it does not recompute, which is what stops a
// list disagreeing with the bar that opened it.
export const MARKET_CHARTS_RECORDS_ENDPOINT = "api/market-charts/records";

// Forward quarters the completion outlook covers by default: through Q4-2027 on
// a Q2-2026 anchor. Longer is offered as an explicit choice, because past 2027
// the taper is the edge of the database rather than the market.
export const MARKET_CHARTS_FORWARD_QUARTERS = 6;

// The EARLIEST quarter the Quarterly Trending Analysis charts end on. The page
// normally ends on the latest CLOSED quarter; this pins it forward so a running
// quarter is shown quarter-to-date (Q3-2026 before it closes on 30 Sep 2026).
// Once the latest closed quarter passes this value it has no effect, so it
// never holds the window back. Bump it to show the next running quarter early.
export const MARKET_CHARTS_MIN_END_QUARTER = "2026Q3";

// Where the completion chart's RECORDED half starts. The chart runs from here
// through the forward horizon, so Q1-2022 on a Q2-2026 anchor is eighteen
// recorded quarters and six scheduled ones.
//
// Read the recorded half as a FLOOR. RPD retires a project once it finishes, so
// the further back the quarter the more of its projects have since dropped out
// of the query — the early bars slope up out of the record-keeping, not out of
// the market. The endpoint returns a history_note saying so and the page prints
// it beside the chart.
export const MARKET_CHARTS_HISTORY_START = "2022Q1";

// The choices the "From" control offers. Each is a Q1 so the recorded half
// always starts on a year boundary — an axis that opens mid-year invites reading
// the first year as short rather than as partial.
export const MARKET_CHARTS_HISTORY_STARTS = ["2022Q1", "2023Q1", "2024Q1", "2025Q1"];

// The same control for an account that is not an admin, and the default it
// opens on. The under-count above is worst in the oldest quarters — by 2022 a
// large share of that year's projects have been retired out of the query — and
// a reader without the caveats in hand reads the slope up out of those bars as
// a market that was recovering. Admins keep the full window because they are
// the ones checking the record; everybody else opens on 2024 and cannot go
// further back.
export const MARKET_CHARTS_HISTORY_START_PUBLIC = "2024Q1";
export const MARKET_CHARTS_HISTORY_STARTS_PUBLIC = ["2024Q1", "2025Q1"];

// ── The RENTAL window, which reaches further back than either floor above ──
//
// Rental is the market this page opens on, and the read it is opened for is the
// long one: lease-up has run through a cycle since 2020 that ten quarters
// cannot show. So a window that includes rental starts here, for every role.
//
// THE FLOORS ABOVE EXIST FOR A REASON AND IT STILL APPLIES. RPD retires a
// project once it finishes, so the further back the quarter the more of its
// projects have dropped out of the query, and rental is the worst case of it:
// 12 rental projects reported in Q1-2024 against 65 in Q2-2026. The climb out
// of 2020 is mostly RISING COVERAGE, not a rising market. That is why the two
// cards that use this floor print the coverage caveat under the chart rather
// than just widening the axis — see comparisonCoverageNote() and
// outlookCoverageNote() in QuarterlyTrendingAnalysis.vue. Widening it without
// the caveat is how a reader concludes the rental market quadrupled.
export const MARKET_CHARTS_RENTAL_HISTORY_START = "2020Q1";

// ── Unit pricing: the Strata history floor, staging only ────
//
// NOT SHARED WITH MARKET_CHARTS_HISTORY_START above, which happens to hold the
// same quarter today. They are floors on different record sets for different
// reasons — that one is the completion chart's retired-project under-count,
// this one is unit-level sale coverage — and one of them moving must not
// silently move the other.
export const MARKET_CHARTS_PRICING_HISTORY_START = "2022Q1";

// WHERE THE FLOOR IS SWITCHED ON. The gate has to live here rather than in the
// endpoint: BACKEND_URL points at production from every environment, so the
// backend sees `www.realpropertydata.ca` whichever front end called it and
// cannot tell staging and localhost apart. The store sends `start` only when
// this is true, and the endpoint has no default — so the production bundle
// simply never asks for a floor and is unaffected by the backend deploy.
//
// Evaluated once at module load. Guarded for the non-browser case (SSR, a unit
// test) so an import of this file cannot throw on a missing `window`.
export const IS_STAGING = (() => {
  if (typeof window === "undefined" || !window.location) return false;
  const host = String(window.location.hostname || "").toLowerCase();
  return host.includes("staging") || host === "localhost" || host === "127.0.0.1";
})();

// Whether an account may use the chat bot: any admin role. The pilot's
// per-address allowlist is gone — 'admin' here is the tier api/get_role
// resolves (administrator, editor, Plan B), the same tier
// presaledb_chatbot_allowed() enforces server-side, so the launcher and the
// endpoints agree on who gets in. Kept as a function so the launcher and the
// route guard cannot drift apart on the test.
// Leaflet basemap tiles, as a base + labels pair per theme. Every Leaflet map
// in the app draws these, which is why they live here beside customMapStyles
// rather than in each component: they were copied into three of them, and when
// the provider changed all three broke separately.
//
// It used to be CARTO's basemap CDN (basemaps.cartocdn.com). Those requests now
// come back as tiles reading "API KEY REQUIRED · carto.com/basemaps/apikey" --
// the maps still drew, they drew the watermark instead of a street. Esri's Gray
// Canvas services need no key and carry a light and a dark variant, so the
// theme switching in each map is unchanged.
//
// Two things differ from CARTO's URL shape and both matter: the path is
// {z}/{y}/{x}, row before column, and there are no {s} subdomains or a {r}
// retina suffix to substitute.
const ESRI_CANVAS = "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas";

export const LEAFLET_BASEMAP = {
    light: {
        base: `${ESRI_CANVAS}/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}`,
        labels: `${ESRI_CANVAS}/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}`,
        labelOpacity: 0.8,
    },
    dark: {
        base: `${ESRI_CANVAS}/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}`,
        labels: `${ESRI_CANVAS}/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}`,
        labelOpacity: 0.7,
    },
};

// Gray Canvas is only rendered to zoom 16. maxNativeZoom stops the layer asking
// for tiles past that -- Leaflet upscales the z16 tile instead, which is a soft
// picture rather than the empty grid a plain maxZoom: 19 would leave behind. It
// matters most on the project maps, which open at street zoom.
//
// `attribution` is set even though these maps are built with attributionControl
// false, so turning the control back on is the only change needed: Esri's terms
// ask for it, and the maps opted out of the control while they were on CARTO.
export const LEAFLET_BASEMAP_OPTIONS = {
    maxZoom: 19,
    maxNativeZoom: 16,
    attribution: "Tiles &copy; Esri",
};

/**
 * Add the base + labels pair for a theme to a Leaflet map, and return them so
 * the caller can remove them on the next theme change.
 *
 * `Lref` is passed in rather than imported: NearbyProjectsTab loads Leaflet
 * lazily and holds its own reference, so there is no one module-level L to use.
 */
export const addLeafletBasemap = (Lref, map, isDark) => {
    const theme = isDark ? LEAFLET_BASEMAP.dark : LEAFLET_BASEMAP.light;
    return [
        Lref.tileLayer(theme.base, LEAFLET_BASEMAP_OPTIONS).addTo(map),
        Lref.tileLayer(theme.labels, {
            ...LEAFLET_BASEMAP_OPTIONS,
            opacity: theme.labelOpacity,
        }).addTo(map),
    ];
};

export const canUseChatBot = (user) => !!user && user.role === "admin";
// SUPER ADMINS — the accounts that may change user access rules (/user-access).
// The backend owns the list (variable rpd_super_admins, see
// user_access_api.inc) and api/current_user returns is_super_admin from it;
// this copy is only the fallback for a backend that predates that flag.
export const SUPER_ADMIN_EMAILS = [
  "darcysrai@gmail.com",
  "erloucabilan94@gmail.com",
  "mark.wang+urban@devradius.com",
  "Brianorito010298@gmail1.com",
  "Brianorito010298@gmail.com",
  "art@realpropertydata.ca",
];
export const isSuperAdmin = (user) => {
  if (!user) return false;
  if (typeof user.is_super_admin === "boolean") return user.is_super_admin;
  return SUPER_ADMIN_EMAILS.map((e) => e.toLowerCase()).includes(String(user.mail || "").toLowerCase());
};

// User access admin API — user_access_api.inc in the presaledb module.
export const ADMIN_USERS_ENDPOINT = "api/admin/users";
export const ADMIN_BULK_ACCESS_ENDPOINT = "api/admin/users/access/bulk";
export const ADMIN_ACCESS_OPTIONS_ENDPOINT = "api/admin/access_options";
export const adminUserEndpoint = (uid) =>
  `api/admin/users/${encodeURIComponent(uid)}`;
export const adminUserAccessEndpoint = (uid) =>
  `api/admin/users/${encodeURIComponent(uid)}/access`;

export const customMapStyles = [
    {
        elementType: "geometry",
        stylers: [
            {
                color: "#f5f5f5",
            },
        ],
    },
    {
        elementType: "labels.icon",
        stylers: [
            {
                visibility: "off",
            },
        ],
    },
    {
        elementType: "labels.text.fill",
        stylers: [
            {
                color: "#616161",
            },
        ],
    },
    {
        elementType: "labels.text.stroke",
        stylers: [
            {
                color: "#f5f5f5",
            },
        ],
    },
    {
        featureType: "administrative.land_parcel",
        elementType: "labels.text.fill",
        stylers: [
            {
                color: "#bdbdbd",
            },
        ],
    },
    {
        featureType: "poi",
        elementType: "geometry",
        stylers: [
            {
                color: "#eeeeee",
            },
        ],
    },
    {
        featureType: "poi",
        elementType: "labels.text.fill",
        stylers: [
            {
                color: "#757575",
            },
        ],
    },
    {
        featureType: "poi.park",
        elementType: "geometry",
        stylers: [
            {
                color: "#e5e5e5",
            },
        ],
    },
    {
        featureType: "poi.park",
        elementType: "labels.text.fill",
        stylers: [
            {
                color: "#9e9e9e",
            },
        ],
    },
    {
        featureType: "road",
        elementType: "geometry",
        stylers: [
            {
                color: "#ffffff",
            },
        ],
    },
    {
        featureType: "road.arterial",
        elementType: "labels.text.fill",
        stylers: [
            {
                color: "#757575",
            },
        ],
    },
    {
        featureType: "road.highway",
        elementType: "geometry",
        stylers: [
            {
                color: "#dadada",
            },
        ],
    },
    {
        featureType: "road.highway",
        elementType: "labels.text.fill",
        stylers: [
            {
                color: "#616161",
            },
        ],
    },
    {
        featureType: "road.local",
        elementType: "labels.text.fill",
        stylers: [
            {
                color: "#9e9e9e",
            },
        ],
    },
    {
        featureType: "transit.line",
        elementType: "geometry",
        stylers: [
            {
                color: "#e5e5e5",
            },
        ],
    },
    {
        featureType: "transit.station",
        elementType: "geometry",
        stylers: [
            {
                color: "#eeeeee",
            },
        ],
    },
    {
        featureType: "water",
        elementType: "geometry",
        stylers: [
            {
                color: "#c9c9c9",
            },
        ],
    },
    {
        featureType: "water",
        elementType: "labels.text.fill",
        stylers: [
            {
                color: "#9e9e9e",
            },
        ],
    },
];
// ── RPD Tasks ───────────────────────────────────────────────
// Work tracker for data operations: roll reconciliation, unit corrections,
// permit sweeps, project onboarding, MLS intake, submission review, report
// builds and platform bugs.
//
// Served by the rpd_tasks module, not presaledb — own tables, own endpoints.
// See sites/all/modules/custom/rpd_tasks/README.md for why it is not a
// content type.
//
// Unlike presaledb's routes, these answer a preflight with
// Access-Control-Allow-Headers: Content-Type, so a normal application/json
// POST works here. The text/plain workaround in MarketOverviewStore.js is not
// needed for anything under api/tasks.
export const TASKS_ME_ENDPOINT = "api/tasks/me";
export const TASKS_META_ENDPOINT = "api/tasks/meta";
export const TASKS_LIST_ENDPOINT = "api/tasks/list";
export const TASKS_GET_ENDPOINT = "api/tasks/get";
export const TASKS_CREATE_ENDPOINT = "api/tasks/create";
export const TASKS_UPDATE_ENDPOINT = "api/tasks/update";
export const TASKS_GATES_ENDPOINT = "api/tasks/gates";
export const TASKS_TRANSITION_ENDPOINT = "api/tasks/transition";
export const TASKS_ASSIGN_ENDPOINT = "api/tasks/assign";
export const TASKS_DUE_ENDPOINT = "api/tasks/due";
export const TASKS_BLOCK_ENDPOINT = "api/tasks/block";
export const TASKS_SUBTASK_ENDPOINT = "api/tasks/subtask";
export const TASKS_PEOPLE_ENDPOINT = "api/tasks/people";
export const TASKS_PERSON_ENDPOINT = "api/tasks/person";
export const TASKS_FILE_UPLOAD_ENDPOINT = "api/tasks/file/upload";
export const TASKS_FILE_DELETE_ENDPOINT = "api/tasks/file/delete";
export const TASKS_FILE_KIND_ENDPOINT = "api/tasks/file/kind";

// The board lanes, left to right. 'blocked' is deliberately not a lane — it is
// a flag on the card, so a blocked card stays in the column where the work
// actually sits and the board never lies about progress.
export const TASK_LANES = [
    { key: "triage", label: "Triage" },
    { key: "ready", label: "Ready" },
    { key: "progress", label: "In Progress" },
    { key: "review", label: "In Review" },
    { key: "done", label: "Done" },
];

// Route guard for /tasks. Anyone the backend resolves to a task level gets in;
// what they may *do* comes from api/tasks/me and, per card, api/tasks/gates.
//
// This only tests that a level exists, so it cannot drift from the server's
// rule the way a reimplemented permission matrix would. The authority is
// rpd_tasks_level() in inc/rpd_tasks.access.inc.
export const canUseTasks = (session) => !!session && !!session.level;
